<script lang="ts">
  import { onMount } from 'svelte'
  import { api, handleApiError } from '../../lib/api'
  import { fade, slide } from 'svelte/transition'
  import { addToast } from '../../lib/toast'

  type Config = {
    id: string
    free_number: string | null
    created_at: string
    updated_at: string
  }

  type FreeSendToken = {
    exists: boolean
    token?: string
    id?: string
    name?: string
    expires_at?: string | null
    created_at?: string
    message?: string
  }

  type AdminSession = {
    id: string
    label: string
    number: string
    connected?: boolean
  }

  let loading = false
  let config: Config | null = null
  let freeSendToken: FreeSendToken | null = null
  let adminSessions: AdminSession[] = []
  let loadingToken = false
  let loadingSessions = false
  let saving = false

  // Blocked IPs
  let blockedIPs: string[] = []
  let loadingBlockedIPs = false
  let unblockingIP = ''

  // Form data
  let selectedFreeNumber = ''
  let customFreeNumber = ''

  onMount(async () => {
    await Promise.all([loadConfig(), loadFreeSendToken(), loadAdminSessions(), loadBlockedIPs()])
  })

  async function loadConfig() {
    loading = true
    try {
      const res = await api.get('/api/v1/admin/config')
      config = res
      if (config?.free_number) {
        selectedFreeNumber = config.free_number
        customFreeNumber = config.free_number
      }
    } catch (e: any) {
      addToast('Error cargando configuración: ' + handleApiError(e), 'error')
    } finally {
      loading = false
    }
  }

  async function loadFreeSendToken() {
    loadingToken = true
    try {
      const res = await api.get('/api/v1/admin/config/free-send-token')
      freeSendToken = res
    } catch (e: any) {
      addToast('Error cargando token: ' + handleApiError(e), 'error')
    } finally {
      loadingToken = false
    }
  }

  async function loadAdminSessions() {
    loadingSessions = true
    try {
      const params = new URLSearchParams({
        page: '1',
        pageSize: '100'
      })
      const res = await api.get(`/api/v1/admin/sessions?${params.toString()}`)
      adminSessions = (res.sessions || []).filter((s: AdminSession) => s.number && s.number !== '')
    } catch (e: any) {
      addToast('Error cargando sesiones: ' + handleApiError(e), 'error')
    } finally {
      loadingSessions = false
    }
  }

  async function saveFreeNumber() {
    if (saving) return

    saving = true
    try {
      const freeNumber = selectedFreeNumber === 'custom' ? customFreeNumber : selectedFreeNumber
      await api.put('/api/v1/admin/config/free-number', {
        free_number: freeNumber === 'none' ? '' : freeNumber
      })
      addToast('Número libre actualizado correctamente', 'success')
      await loadConfig()
    } catch (e: any) {
      addToast('Error guardando: ' + handleApiError(e), 'error')
    } finally {
      saving = false
    }
  }

  async function createOrRegenerateToken() {
    if (loadingToken) return

    loadingToken = true
    try {
      const res = await api.post('/api/v1/admin/config/free-send-token', {})
      freeSendToken = {
        exists: true,
        token: res.token,
        id: res.id,
        name: res.name,
        expires_at: res.expires_at,
        created_at: res.created_at
      }
      addToast('Token creado/regenerado correctamente', 'success')
    } catch (e: any) {
      addToast('Error creando token: ' + handleApiError(e), 'error')
    } finally {
      loadingToken = false
    }
  }

  function copyToken() {
    if (!freeSendToken?.token) return
    navigator.clipboard.writeText(freeSendToken.token)
    addToast('Token copiado al portapapeles', 'success')
  }

  function maskToken(token: string): string {
    if (token.length <= 8) return '****'
    return token.substring(0, 4) + '****' + token.substring(token.length - 4)
  }

  async function loadBlockedIPs() {
    loadingBlockedIPs = true
    try {
      const res = await api.get('/api/v1/admin/config/blocked-ips')
      blockedIPs = res.ips || []
    } catch (e: any) {
      addToast('Error cargando IPs bloqueadas: ' + handleApiError(e), 'error')
    } finally {
      loadingBlockedIPs = false
    }
  }

  async function unblockIP(ip: string) {
    if (unblockingIP) return

    unblockingIP = ip
    try {
      await api.delete(`/api/v1/admin/config/blocked-ips/${encodeURIComponent(ip)}`)
      addToast(`IP ${ip} desbloqueada correctamente`, 'success')
      await loadBlockedIPs()
    } catch (e: any) {
      addToast('Error desbloqueando IP: ' + handleApiError(e), 'error')
    } finally {
      unblockingIP = ''
    }
  }
</script>

<div class="p-6 space-y-6">
  <!-- Header -->
  <div class="flex items-center justify-between">
    <div>
      <h1 class="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-pink-300 via-purple-300 to-indigo-300">
        Configuración - Envío Libre
      </h1>
      <p class="text-indigo-200/60 text-sm mt-1">Gestiona el token y número para la API de envío libre</p>
    </div>
  </div>

  {#if loading}
    <div class="flex items-center justify-center py-12">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
    </div>
  {:else}
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <!-- Token Section -->
      <div class="bg-black/30 border border-white/10 rounded-2xl p-6 shadow-xl">
        <div class="flex items-center justify-between mb-4">
          <h2 class="text-xl font-bold text-white">Token de Envío Libre</h2>
          <span class="px-3 py-1 rounded-full text-xs font-bold bg-purple-500/20 text-purple-300 border border-purple-500/30">
            API Token
          </span>
        </div>

        <p class="text-sm text-indigo-200/60 mb-6">
          Token específico para la API de envío libre desde app móvil Android
        </p>

        {#if loadingToken}
          <div class="flex items-center justify-center py-8">
            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
          </div>
        {:else if freeSendToken?.exists && freeSendToken.token}
          <!-- Token existente -->
          <div class="space-y-4">
            <div>
              <label class="block text-xs font-bold text-indigo-200 uppercase tracking-wider mb-2">
                Token
              </label>
              <div class="flex gap-2">
                <input
                  type="text"
                  value={freeSendToken.token}
                  readonly
                  class="flex-1 px-4 py-3 rounded-xl bg-black/30 border border-white/10 text-white font-mono text-sm"
                />
                <button
                  on:click={copyToken}
                  class="px-4 py-3 rounded-xl bg-purple-500/20 text-purple-300 border border-purple-500/30 hover:bg-purple-500/30 transition-colors"
                  title="Copiar"
                >
                  📋
                </button>
              </div>
            </div>

            {#if freeSendToken.expires_at}
              <div class="text-xs text-indigo-200/60">
                <span class="font-semibold">Expira:</span> {new Date(freeSendToken.expires_at).toLocaleString()}
              </div>
            {:else}
              <div class="text-xs text-emerald-300/70">
                <span class="font-semibold">Sin expiración</span>
              </div>
            {/if}

            <button
              on:click={createOrRegenerateToken}
              disabled={loadingToken}
              class="w-full px-4 py-3 rounded-xl bg-gradient-to-r from-orange-500 via-red-500 to-pink-500 hover:from-orange-400 hover:via-red-400 hover:to-pink-400 text-white font-bold shadow-lg shadow-orange-500/30 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loadingToken ? 'Regenerando...' : '🔄 Regenerar Token'}
            </button>
          </div>
        {:else}
          <!-- No hay token -->
          <div class="space-y-4">
            <div class="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4">
              <p class="text-sm text-yellow-300/80">
                No hay token configurado. Crea uno para habilitar la API de envío libre.
              </p>
            </div>
            <button
              on:click={createOrRegenerateToken}
              disabled={loadingToken}
              class="w-full px-4 py-3 rounded-xl bg-gradient-to-r from-purple-500 via-pink-500 to-indigo-500 hover:from-purple-400 hover:via-pink-400 hover:to-indigo-400 text-white font-bold shadow-lg shadow-purple-500/30 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loadingToken ? 'Creando...' : '✨ Crear Token'}
            </button>
          </div>
        {/if}
      </div>

      <!-- Free Number Section -->
      <div class="bg-black/30 border border-white/10 rounded-2xl p-6 shadow-xl">
        <div class="flex items-center justify-between mb-4">
          <h2 class="text-xl font-bold text-white">Número Libre del Admin</h2>
          <span class="px-3 py-1 rounded-full text-xs font-bold bg-blue-500/20 text-blue-300 border border-blue-500/30">
            Sesión
          </span>
        </div>

        <p class="text-sm text-indigo-200/60 mb-6">
          Selecciona la sesión del admin que se usará para envío libre. Si no hay número configurado, se usará la primera sesión disponible.
        </p>

        {#if loadingSessions}
          <div class="flex items-center justify-center py-8">
            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        {:else}
          <div class="space-y-4">
            <div>
              <label class="block text-xs font-bold text-indigo-200 uppercase tracking-wider mb-2">
                Seleccionar Sesión
              </label>
              <select
                bind:value={selectedFreeNumber}
                class="w-full px-4 py-3 rounded-xl bg-black/30 border border-white/10 text-white focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all outline-none"
              >
                <option value="none">Ninguna (usar primera disponible)</option>
                {#each adminSessions as session}
                  <option value={session.number}>
                    {session.label} ({session.number}) {session.connected ? '✅' : '❌'}
                  </option>
                {/each}
                <option value="custom">Ingresar número personalizado</option>
              </select>
            </div>

            {#if selectedFreeNumber === 'custom'}
              <div>
                <label class="block text-xs font-bold text-indigo-200 uppercase tracking-wider mb-2">
                  Número Personalizado
                </label>
                <input
                  type="text"
                  bind:value={customFreeNumber}
                  placeholder="Ej: 913980635"
                  class="w-full px-4 py-3 rounded-xl bg-black/30 border border-white/10 text-white focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all outline-none placeholder-white/20"
                />
              </div>
            {/if}

            <button
              on:click={saveFreeNumber}
              disabled={saving}
              class="w-full px-4 py-3 rounded-xl bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 hover:from-blue-400 hover:via-indigo-400 hover:to-purple-400 text-white font-bold shadow-lg shadow-blue-500/30 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Guardando...' : '💾 Guardar Configuración'}
            </button>

            {#if config?.free_number}
              <div class="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-3">
                <p class="text-xs text-emerald-300/80">
                  <span class="font-semibold">Actual:</span> {config.free_number}
                </p>
              </div>
            {/if}
          </div>
        {/if}
      </div>
    </div>

    <!-- Blocked IPs Section -->
    <div class="bg-black/30 border border-white/10 rounded-2xl p-6 shadow-xl">
      <div class="flex items-center justify-between mb-4">
        <h2 class="text-xl font-bold text-white">IPs Bloqueadas</h2>
        <span class="px-3 py-1 rounded-full text-xs font-bold bg-red-500/20 text-red-300 border border-red-500/30">
          {blockedIPs.length} {blockedIPs.length === 1 ? 'IP' : 'IPs'}
        </span>
      </div>

      <p class="text-sm text-indigo-200/60 mb-6">
        IPs que han sido bloqueadas permanentemente por errores consecutivos en la API de envío libre.
      </p>

      {#if loadingBlockedIPs}
        <div class="flex items-center justify-center py-8">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500"></div>
        </div>
      {:else if blockedIPs.length === 0}
        <div class="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 text-center">
          <p class="text-sm text-emerald-300/80">
            No hay IPs bloqueadas
          </p>
        </div>
      {:else}
        <div class="space-y-2">
          {#each blockedIPs as ip}
            <div class="flex items-center justify-between bg-black/40 border border-white/10 rounded-xl p-4">
              <div class="flex items-center gap-3">
                <span class="text-2xl">🚫</span>
                <div>
                  <p class="text-white font-mono font-semibold">{ip}</p>
                  <p class="text-xs text-indigo-200/50">Bloqueada permanentemente</p>
                </div>
              </div>
              <button
                on:click={() => unblockIP(ip)}
                disabled={unblockingIP === ip}
                class="px-4 py-2 rounded-xl bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-bold text-sm"
              >
                {unblockingIP === ip ? 'Desbloqueando...' : '🔓 Desbloquear'}
              </button>
            </div>
          {/each}
        </div>
      {/if}

      <div class="mt-4">
        <button
          on:click={loadBlockedIPs}
          disabled={loadingBlockedIPs}
          class="w-full px-4 py-2 rounded-xl bg-indigo-500/20 text-indigo-300 border border-indigo-500/30 hover:bg-indigo-500/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-bold text-sm"
        >
          {loadingBlockedIPs ? 'Cargando...' : '🔄 Actualizar Lista'}
        </button>
      </div>
    </div>

    <!-- Info Section -->
    <div class="bg-black/30 border border-white/10 rounded-2xl p-6 shadow-xl">
      <h3 class="text-lg font-bold text-white mb-4">ℹ️ Información</h3>
      <div class="space-y-3 text-sm text-indigo-200/70">
        <div>
          <span class="font-semibold text-white">Token:</span> Se usa para autenticar requests desde la app móvil Android. Solo se aceptan requests desde dispositivos Android.
        </div>
        <div>
          <span class="font-semibold text-white">Número Libre:</span> Si está configurado, se usará esa sesión específica. Si no, se usará la primera sesión del admin disponible y conectada.
        </div>
        <div>
          <span class="font-semibold text-white">Bloqueo de IP:</span> Después de 3 errores consecutivos, la IP se bloquea permanentemente. Puedes desbloquearlas desde esta sección.
        </div>
      </div>
    </div>
  {/if}
</div>

